<template>
  <label v-if="field.label !== false" :for="field.name">
    {{ field.label || field.name }}
  </label>
  <component
    :is="props.formComponent || AutoForm"
    v-if="field.type === 'sub'"
    @update:modelValue="handleUpdate($event)"
    :modelValue="object"
    :fields="field.fields"
    :errors="errors"
    :sub="true"
    :class="field.css_classes"
  />
  <div v-else-if="field.type === 'inline'" class="inline-form">
    <Card v-for="row in object[field.name]">
      <template #content>
        <div class="row">
          <component
            :is="props.formComponent || AutoForm"
            :key="row.id"
            :modelValue="row"
            :defaults="defaults"
            :errors="errors"
            :fields="field.fields"
            sub
            @update:modelValue="handleInlineUpdate(row.id, $event)"
          />
          <Button @click="handleInlineRemove(row.id)" icon="pi pi-times" />
          <!-- :label="`Remove ${row.id}`"  -->
        </div>
      </template>
    </Card>
    <Card v-if="!field.inlineDisableAdd" class="add-another">
      <template #content>
        <component
          :is="props.formComponent"
          :key="'new'"
          :modelValue="{}"
          :defaults="defaults"
          :errors="errors"
          :fields="field.fields"
          sub
          @update:modelValue="handleInlineUpdate(null, $event)"
        />
      </template>
    </Card>
  </div>
  <Dropdown
    v-else-if="(!field.type && field.options) || field.type === 'select'"
    @update:modelValue="update($event)"
    @focus="field.focus"
    :required="field.required === undefined ? true : field.required"
    :readonly="field.readonly"
    :tabindex="field.tabindex"
    :class="field.css_classes || 'form-control'"
    :options="field.options"
    :modelValue="object[field.name]"
    :invalid="!!(errors && errors[field.name])"
    :placeholder="field.placeholder"
    :aria-label="field.ariaLabel"
    :disabled="field.disabled"
    style="position: relative"
    optionLabel="name"
    optionValue="value"
  />
  <div v-else-if="field.type === 'checkbox-list'" class="checkbox-list">
    <label v-for="option of field.options" :key="option.value">
      <Checkbox
        @change="update($event.target.checked, option.value)"
        :value="option.value"
        :readonly="field.readonly"
        :checked="
          object[field.name] &&
          (object[field.name].indexOf(option.value) > -1 ||
            object[field.name][option.value])
        "
        :invalid="!!(errors && errors[field.name])"
        :aria-label="field.ariaLabel"
        :disabled="field.disabled"
      />
      {{ option.name || option.label }}
    </label>
  </div>
  <div v-else-if="field.type === 'color'" class="color-row">
    <ColorPicker
      @update:modelValue="update($event)"
      :modelValue="object[field.name]"
      :name="field.name"
      :placeholder="field.placeholder"
      :readonly="field.readonly"
      :tabindex="field.tabindex"
      :required="field.required === undefined ? true : field.required"
      :class="field.css_classes || 'form-control'"
    />
    <InputText
      @update:modelValue="update($event)"
      :modelValue="object[field.name]"
      :invalid="!!(errors && errors[field.name])"
      :style="{ width: '6em' }"
      :name="field.name"
      :placeholder="field.placeholder"
      :readonly="field.readonly"
      :tabindex="field.tabindex"
      :required="field.required === undefined ? true : field.required"
      :class="field.css_classes || 'form-control'"
      :aria-label="field.ariaLabel"
      :disabled="field.disabled"
      type="text"
      maxlength="6"
    />
  </div>
  <div v-else-if="field.type === 'checkbox'" class="checkbox">
    <Checkbox
      :modelValue="object[field.name]"
      :binary="true"
      :readonly="field.readonly"
      :invalid="!!(errors && errors[field.name])"
      :aria-label="field.ariaLabel"
      :disabled="field.disabled"
      @update:modelValue="update($event)"
    />
  </div>
  <Textarea
    v-else-if="field.type === 'textarea'"
    @input="update($event.target.value)"
    :modelValue="object[field.name]"
    :rows="field.rows || 3"
    :cols="field.cols || 23"
    :name="field.name"
    :placeholder="field.placeholder"
    :readonly="field.readonly"
    :tabindex="field.tabindex"
    :required="field.required === undefined ? true : field.required"
    :class="field.css_classes || 'form-control'"
    :invalid="!!(errors && errors[field.name])"
    :aria-label="field.ariaLabel"
    :disabled="field.disabled"
  />
  <div v-else-if="field.type === 'range'">
    <InputNumber
      @update:modelValue="update($event)"
      :modelValue="object[field.name]"
      :name="field.name"
      :placeholder="field.placeholder"
      :readonly="field.readonly"
      :tabindex="field.tabindex"
      :min="field.min"
      :max="field.max"
      :step="field.step"
      :required="field.required === undefined ? true : field.required"
      :class="field.css_classes || 'form-control'"
      :invalid="!!(errors && errors[field.name])"
      :aria-label="field.ariaLabel"
      :disabled="field.disabled"
    />
    <Slider
      @update:modelValue="update($event)"
      :modelValue="parseFloat(object[field.name])"
      :name="field.name"
      :placeholder="field.placeholder"
      :readonly="field.readonly"
      :tabindex="field.tabindex"
      :min="field.min"
      :max="field.max"
      :step="field.step"
      :required="field.required === undefined ? true : field.required"
      :class="field.css_classes || 'form-control'"
      :invalid="!!(errors && errors[field.name])"
      :aria-label="field.ariaLabel"
    />
  </div>
  <InputNumber
    v-else-if="field.type === 'number'"
    @update:modelValue="update($event)"
    :modelValue="object[field.name]"
    :size="field.size"
    :name="field.name"
    :placeholder="field.placeholder"
    :readonly="field.readonly"
    :tabindex="field.tabindex"
    :min="field.min"
    :max="field.max"
    :step="field.step"
    :required="field.required === undefined ? true : field.required"
    :class="field.css_classes || 'form-control'"
    :invalid="!!(errors && errors[field.name])"
    :aria-label="field.ariaLabel"
    :disabled="field.disabled"
  />
  <InputText
    v-else
    @input="update($event.target.value)"
    :modelValue="object[field.name]"
    :type="field.type"
    :size="field.size"
    :name="field.name"
    :autocomplete="field.autocomplete"
    :placeholder="field.placeholder"
    :readonly="field.readonly"
    :tabindex="field.tabindex"
    :maxlength="field.maxlength"
    :pattern="field.pattern"
    :required="field.required === undefined ? true : field.required"
    :class="field.css_classes || 'form-control'"
    :invalid="!!(errors && errors[field.name])"
    :aria-label="field.ariaLabel"
    :disabled="field.disabled"
  />
</template>

<script setup lang="ts">
import copy from "@virgodev/bazaar/functions/copy";
import Button from "primevue/button";
import Card from "primevue/card";
import Checkbox from "primevue/checkbox";
import ColorPicker from "primevue/colorpicker";
import Dropdown from "primevue/dropdown";
import InputNumber from "primevue/inputnumber";
import InputText from "primevue/inputtext";
import Slider from "primevue/slider";
import Textarea from "primevue/textarea";
import type { Component } from "vue";
import { computed } from "vue";
import { FieldDef, updateModelValue } from "./defs";

import AutoForm from "./AutoForm.vue";

defineOptions({ inheritAttrs: false });
const emit = defineEmits(["update:modelValue"]);

const props = defineProps<{
  field: FieldDef;
  modelValue: Object;
  formComponent?: Component;
  errors?: Object;
  defaults?: Object;
}>();

const object = computed(() => {
  const retval = copy(props.modelValue);

  // if the value isn't an option, remove it
  if (props.field.options && retval[props.field.name]) {
    if (
      !props.field.options.find((o) => o.value === retval[props.field.name])
    ) {
      delete retval[props.field.name];
    }
  }

  // match defaults
  if (!retval[props.field.name] && props.field.default) {
    retval[props.field.name] = props.field.default;
  }
  if (props.defaults) {
    for (const key in props.defaults) {
      if (retval[key] !== false && !retval[key]) {
        retval[key] = props.defaults[key];
      }
    }
  }
  return retval;
});

function handleInlineUpdate(id, data) {
  const modelValue = copy(props.modelValue);
  if (!modelValue[props.field.name]) {
    modelValue[props.field.name] = [];
  }
  if (!id) {
    modelValue[props.field.name].push(data);
  } else {
    const idx = modelValue[props.field.name].findIndex((r) => r.id === id);
    modelValue[props.field.name][idx] = data;
  }
  emit("update:modelValue", modelValue);
}
function handleInlineRemove(id) {
  const modelValue = copy(props.modelValue);
  if (!modelValue[props.field.name]) {
    modelValue[props.field.name] = [];
  }
  const idx = modelValue[props.field.name].findIndex((r) => r.id === id);
  modelValue[props.field.name].splice(idx, 1);
  emit("update:modelValue", modelValue);
}
function handleUpdate(values) {
  emit("update:modelValue", values);
}
function update(value, key) {
  emit(
    "update:modelValue",
    updateModelValue(props.modelValue, props.field, value, key)
  );
}
</script>

<style scoped>
.p-colorpicker {
  padding: 5px;
}
.p-slider {
  margin-top: 15px;
  margin-left: 15px;
}
.checkbox {
  margin-top: 15px;
  margin-left: 5px;
}
.row {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: flex-start;
}
</style>
